import { type CSSProperties, type FC, memo, type PropsWithChildren, useMemo } from "react";
import { css } from "@stubhub/broadway-foundation/css";
import { Skeleton, SkeletonShape } from "@viagogo/core/ui";
import { CALENDAR_CSS_VARIABLES } from "src/website-apps/responsive/stubhub-performer/components/calendar/styles";

const skeletonDateHeader = css({
  marginBlockEnd: "primitives.100",
});

const skeletonCardBody = css({
  display: "flex",
  alignItems: "center",
  paddingBlock: "primitives.100",
  paddingInline: "primitives.200",
  columnGap: "primitives.100",
  rowGap: "primitives.100",
  borderBlockStartWidth: "primitives.25",
  borderBlockEndWidth: "primitives.25",
  borderInlineStartWidth: "primitives.25",
  borderInlineEndWidth: "primitives.25",
  borderBlockStartStyle: "solid",
  borderBlockEndStyle: "solid",
  borderInlineStartStyle: "solid",
  borderInlineEndStyle: "solid",
  borderBlockStartColor: "border",
  borderBlockEndColor: "border",
  borderInlineStartColor: "border",
  borderInlineEndColor: "border",
  borderStartStartRadius: "container",
  borderStartEndRadius: "container",
  borderEndStartRadius: "container",
  borderEndEndRadius: "container",
});

const skeletonEventInfo = css({
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: "primitives.0",
  display: "flex",
  flexDirection: "column",
  columnGap: "primitives.50",
  rowGap: "primitives.50",
});

interface Props {
  primaryColor?: CSSProperties["color"];
  loading?: boolean;
  ariaLabel?: string | undefined;
}

export const BaseDateCardMobile: FC<PropsWithChildren<Props>> = memo(
  ({ children, primaryColor, loading, ariaLabel }) => {
    const containerStyle: CSSProperties = useMemo(
      () => ({
        [CALENDAR_CSS_VARIABLES.primaryColor]: primaryColor ?? "transparent",
        textAlign: "left",
      }),
      [primaryColor],
    );

    if (loading) {
      return (
        <div style={containerStyle} aria-hidden>
          <div className={skeletonDateHeader}>
            <Skeleton width="60%" height="16px" shape={SkeletonShape.rounded} />
          </div>
          <div className={skeletonCardBody}>
            <Skeleton width="40px" height="40px" shape={SkeletonShape.circular} />
            <div className={skeletonEventInfo}>
              <Skeleton width="70%" height="16px" shape={SkeletonShape.rounded} />
              <Skeleton width="50%" height="14px" shape={SkeletonShape.rounded} />
            </div>
            <Skeleton width="80px" height="32px" shape={SkeletonShape.rounded} />
          </div>
        </div>
      );
    }

    const isEmpty = !children;
    if (isEmpty) {
      return null;
    }

    return (
      <div style={containerStyle} aria-label={ariaLabel}>
        <div>{children}</div>
      </div>
    );
  },
);
BaseDateCardMobile.displayName = "BaseDateCardMobile";
